import { parseProjectilePool } from './projectile-targeting';
import { paintedWallProjectileKind, type WallProjectileKind } from './painted-wall-rules';
export const ABILITY_TARGETING_SCHEMA = 'ft.ability-targeting.intent.v1' as const;
export const ABILITY_TARGETING_PICK_SCHEMA = 'ft.ability-targeting.pick.v1' as const;
export const ABILITY_TARGETING_CANDIDATES_SCHEMA = 'ft.ability-targeting.candidates.v1' as const;
export const ABILITY_TARGETING_FEEDBACK_SCHEMA = 'ft.ability-targeting.feedback.v1' as const;
export const ABILITY_TARGETING_ESTIMATES_SCHEMA = 'ft.ability-targeting.estimates.v1' as const;

const RANGE_SELF_ID = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
const RANGE_OBJECT_ID = '7E615545-1179-6F6A-9729-0AFFCD588B31';
const CONTESTING_ROLL_ID = 'AD80D31D-898B-66CE-0894-1335D11B1529';
const RANGED_ATTACK_ROLL_IDS = Object.freeze([
  '3F85CA97-F9B2-A83A-FDE8-E523FD99346C', // weapon ranged attack roll
  'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C', // spell ranged attack roll
]);
// Melee attacks aim too (2026-08-26): flanking is attack-time C–T–A geometry, previewed on hover.
const MELEE_ATTACK_ROLL_IDS = Object.freeze([
  '2FE8DFB7-FE61-4862-AF71-3523262350A0', // weapon melee attack roll
  '5740CA49-006E-A7A0-B46B-6DDED6CD546C', // spell melee attack roll
]);
export const ABILITY_TARGETING_AIM_LINES = Object.freeze(['none', 'line-of-effect', 'ranged-attack', 'melee-attack'] as const);
export type AbilityTargetingAimLine = typeof ABILITY_TARGETING_AIM_LINES[number];
export function normalizeAbilityTargetingAimLine(value: unknown): AbilityTargetingAimLine {
  return value === 'line-of-effect' || value === 'ranged-attack' || value === 'melee-attack' ? value : 'none';
}
const RANGE_FEET_BY_ID: Readonly<Record<string, number | null>> = Object.freeze({
  '36D59FF4-6778-96ED-8664-F5D63638C77B': null, // reach: caster-dependent
  '4C761395-2532-3F23-7CBA-CA81AF5C0855': 30,
  'A95F219A-6288-6B17-6DB7-03DE4B97AE96': 60,
  'D1E8DA38-61C2-97AA-6173-4BF6157FEEE3': 120,
  'F6DAE84B-CBB3-5C0F-89EF-370DE841DFDA': 240,
  'BB47D2E8-FFFD-8A04-FC6C-FC02583750ED': null, // unlimited
});

/** Authored feet for a range id: a number, Infinity for unlimited, `null` for reach (caster-dependent)
 *  and `undefined` for ids outside the numeric table (self, object range, touch-style customs). */
export function abilityRangeFeetForId(rangeId: string): number | null | undefined {
  const id = String(rangeId ?? '').trim().toUpperCase();
  if (id === 'BB47D2E8-FFFD-8A04-FC6C-FC02583750ED') return Infinity;
  return Object.prototype.hasOwnProperty.call(RANGE_FEET_BY_ID, id) ? RANGE_FEET_BY_ID[id] : undefined;
}

const AUTO_TARGET_LAYERS = Object.freeze([
  'target_random_enemy',
  'target_nearest_enemy',
  'target_lowest_vitality_enemy',
  'target_all_enemies',
  'fabricate',
]);

export interface AbilityTargetingProfile {
  kind: 'creature-targets';
  supportsInventoryTargets?: boolean;
  allowInventoryTargets?: boolean;
  /** Authored world-object targets, always enabled and restricted to these item types. */
  inventoryTargetTypes?: string[];
  /** Held-item imbue (Frostwind Blade): the creature click opens a radial of that creature's EQUIPPED
   *  slots and the chosen row's inventory id becomes the target. candidateEntityIds carries the HOLDER
   *  creature ids only; the chosen row is resolved to its holder at pick time (selectHeldItem), and
   *  range/LOE are measured to the holder. */
  heldItemTargetTypes?: string[];
  aimLine: AbilityTargetingAimLine;
  projectileKind?: WallProjectileKind;
  minTargets: number;
  maxTargets: number;
  projectilePool?: import('./projectile-targeting').ProjectilePool;
  rangeId: string;
  rangeFeet: number | null;
  rangeLabel: string;
  /** A thrown weapon switched to Lob (owner 2026-09-15): the scene traces the flat 45° arc to the target
   *  instead of the straight line, and the cast posts `throw_lob=1` for Janus to re-judge. */
  throwLob?: boolean;
  /** Exact weapon/mode armed for this draft, also used by the caster's hit previews. */
  weaponAttack?: { inventoryId: string; throwing: boolean };
}

export type AbilityTargetingProfileResult =
  | { supported: true; profile: AbilityTargetingProfile }
  | {
    supported: false;
    reason:
      | 'missing-ability-id'
      | 'aoe'
      | 'self-target'
      | 'object-target'
      | 'targetless-or-implicit'
      | 'runtime-target-count'
      | 'invalid-target-count'
      | 'auto-target'
      | 'item-target'
      | 'unresolved-range';
  };

export interface AbilityDispatchFacts {
  aoe?: boolean;
  inputId?: string;
  /** Semantic family of inputId when the caller has the ability blob. The canonical id alone is
   * ambiguous because `_spell_upcastable` also carries variable targets/charge spend. */
  inputKind?: 'spell-level' | 'numeric' | 'damage-type' | 'other';
  trigger?: boolean;
  dedicated?: boolean;
  weaponPinned?: boolean;
  itemTarget?: boolean;
  markerOrSource?: boolean;
  specialized?: boolean;
}

export type AbilityDispatchClassification =
  | { route: 'ability-targeting'; invocation: AbilityTargetingInvocationShape }
  | { route: 'legacy'; reason: string };

export type AbilityTargetingInvocationShape =
  | { kind: 'plain' }
  | { kind: 'variable-1-number'; inputId: string }
  | { kind: 'variable-1-choice'; inputId: string };

export type AbilityTargetingNumericOptionKind = 'spell-level' | 'variable-targets'
  | 'variable-charges' | 'variable-mana';

export type AbilityTargetingInvocation =
  | { kind: 'plain'; abilityId: string }
  | { kind: 'variable-1-number'; abilityId: string; inputId: string; value: number; secondaryChoice?: string }
  | { kind: 'variable-1-choice'; abilityId: string; inputId: string; value: string };

export interface ActiveAbilityTargetingIntent {
  intentId: number;
  casterId: string;
  abilityId: string;
  abilityName: string;
  /** Numeric AP paid by the ability itself. Reactions/non-AP actions use null. */
  actionPointCost: number | null;
  profile: AbilityTargetingProfile;
  invocation: AbilityTargetingInvocation;
  phase: 'selecting-targets' | 'ready-to-confirm';
  selectedEntityIds: string[];
  candidateEntityIds: string[];
  /**
   * Every `source` the caster's grant rows name for this ability (the object that surfaced it). The scene
   * moves the ability's ORIGIN onto one of these only when the caster is piloting it right now — a war
   * machine's controls fire from the machine, never from the operator seated inside its volume. Mirrors
   * ft_spatial_action_origin_arr() in Janus; any other source (an equipped item) is ignored.
   */
  grantSourceIds?: string[];
  /**
   * D-050 (owner 2026-09-30): CAST-THROUGH-SUMMON — the caster's controlled summons this spell may measure its range from
   * (is_target_in_range(): the closest, when strictly closer than the caster). The scene judges range AND line of effect from that origin.
   */
  rangeOriginIds?: string[];
  /** Per rangeOriginIds entry: its allowed feet when the range is the REACH band (the summon's own reach × the Distant multiplier);
   *  null / absent = the profile's rangeFeet. */
  rangeOriginFeet?: Array<number | null>;
}

export interface AbilityTargetingIntentEnvelope {
  schema: typeof ABILITY_TARGETING_SCHEMA;
  version: number;
  intent: Omit<ActiveAbilityTargetingIntent, 'invocation'> | null;
}

export interface AbilityTargetingPickEnvelope {
  schema: typeof ABILITY_TARGETING_PICK_SCHEMA;
  version: number;
  entityId: string;
  pickId: string;
}

export type AbilityTargetingCoverTier = 'none' | 'partial' | 'heavy' | 'blocked';
export type AbilityTargetingRangeState = 'in-range' | 'out-of-range' | 'unknown';

export interface AbilityTargetingCandidateFact {
  entityId: string;
  targetable: boolean;
  rangeState: AbilityTargetingRangeState;
  lineOfEffect: 'clear' | 'blocked';
  coverTier: AbilityTargetingCoverTier;
  effectiveDefenseDelta: number;
  hunkerApplied: boolean;
  /** Melee only: an ally of the caster stands on the far side of this target (+1 ADV, server-authoritative). */
  flanked: boolean;
  flankingAllyName: string | null;
}

export interface AbilityTargetingCandidatesEnvelope {
  schema: typeof ABILITY_TARGETING_CANDIDATES_SCHEMA;
  version: number;
  entityIds: string[];
  candidates: AbilityTargetingCandidateFact[];
}

export type AbilityTargetingFeedbackReason = 'out-of-range' | 'blocked-line-of-effect' | 'incompatible-target' | 'max-targets';

export interface AbilityTargetingFeedbackEnvelope {
  schema: typeof ABILITY_TARGETING_FEEDBACK_SCHEMA;
  version: number;
  reason: AbilityTargetingFeedbackReason;
  /** Optional scene-authored sentence naming the concrete cause (e.g. why a Reach approach was refused). */
  message?: string;
}

export interface AbilityTargetingAimEstimate {
  entityId: string;
  hitChancePercent: number;
  /** Net FateTide advantage (>0) or disadvantage (<0) stacks used by the preview estimate. */
  advDisStacks: number;
}

export interface AbilityTargetingEstimatesEnvelope {
  schema: typeof ABILITY_TARGETING_ESTIMATES_SCHEMA;
  version: number;
  estimates: AbilityTargetingAimEstimate[];
}

export interface AbilityTargetingSkillAttackEstimateInput {
  tierDie: number;
  flatModifier: number;
  advDisStacks: number;
  defense: number;
}

/** UI estimate for FateTide's d20 + trained skill-tier die attack roll against target EC. */
export function estimateSkillAttackHitChance(input: AbilityTargetingSkillAttackEstimateInput): number {
  const tierDie = Math.max(0, Math.min(100, Math.trunc(input.tierDie)));
  const flatModifier = Math.trunc(input.flatModifier);
  const defense = Math.max(0, Math.trunc(input.defense));
  // FateTide stacks ADV/DIS rather than collapsing it to a boolean. Keep the preview faithful to
  // that net stack count; the high guard is only a malformed-input safety valve.
  const rawStacks = Number.isFinite(input.advDisStacks) ? Math.trunc(input.advDisStacks) : 0;
  const stacks = Math.max(-100, Math.min(100, rawStacks));
  const dice = 1 + Math.abs(stacks);
  let chance = 0;
  for (let natural = 1; natural <= 20; natural += 1) {
    const naturalProbability = stacks > 0
      ? (natural / 20) ** dice - ((natural - 1) / 20) ** dice
      : stacks < 0
        ? ((21 - natural) / 20) ** dice - ((20 - natural) / 20) ** dice
        : 1 / 20;
    if (natural === 1) continue;
    if (natural === 20) {
      chance += naturalProbability;
      continue;
    }
    if (tierDie <= 0) {
      if (natural + flatModifier >= defense) chance += naturalProbability;
      continue;
    }
    const minimumTierRoll = Math.max(1, defense - flatModifier - natural);
    const successfulFaces = Math.max(0, tierDie - minimumTierRoll + 1);
    chance += naturalProbability * (successfulFaces / tierDie);
  }
  return Math.max(0, Math.min(100, Math.round(chance * 100)));
}

/**
 * A single browser gesture can be observed by overlapping/persistent FT3D viewer listeners during a
 * scene remount. Suppress only an immediate duplicate for the same intent/entity; a deliberate later
 * click remains a normal toggle.
 */
export class AbilityTargetingPickDeduper {
  private last: { intentId: number; entityId: string; atMs: number } | null = null;
  private readonly seenReceipts = new Set<string>();
  private readonly duplicateWindowMs: number;

  constructor(duplicateWindowMs = 250) {
    this.duplicateWindowMs = duplicateWindowMs;
  }

  accept(intentId: number, rawEntityId: string, pickId = '', atMs = Date.now()): boolean {
    const entityId = String(rawEntityId ?? '').trim().toUpperCase();
    if (!Number.isInteger(intentId) || !entityId || !Number.isFinite(atMs)) return false;
    const receipt = String(pickId ?? '').trim();
    if (receipt) {
      const key = `${intentId}|${entityId}|${receipt}`;
      if (this.seenReceipts.has(key)) return false;
      this.seenReceipts.add(key);
      return true;
    }
    const duplicate = this.last?.intentId === intentId
      && this.last.entityId === entityId
      && atMs >= this.last.atMs
      && atMs - this.last.atMs <= this.duplicateWindowMs;
    if (duplicate) return false;
    this.last = { intentId, entityId, atMs };
    return true;
  }

  reset(): void {
    this.last = null;
    this.seenReceipts.clear();
  }
}

function recordOf(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function scalar(value: unknown): unknown {
  return Array.isArray(value) ? value[0] : value;
}

function itemField(blob: unknown, key: string): unknown {
  const root = recordOf(blob);
  const items = recordOf(root?.items);
  return scalar(items?.[key] ?? root?.[key]);
}

function layerValue(blob: unknown, key: string): unknown {
  const root = recordOf(blob);
  const layers = recordOf(root?.item_layer_types);
  return scalar(layers?.[key] ?? root?.[key]);
}

function layerPresent(blob: unknown, key: string): boolean {
  const root = recordOf(blob);
  const layers = recordOf(root?.item_layer_types);
  return Object.prototype.hasOwnProperty.call(layers ?? {}, key)
    || Object.prototype.hasOwnProperty.call(root ?? {}, key);
}

function enabled(value: unknown): boolean {
  return value !== undefined && value !== null && value !== false && value !== 0
    && value !== '0' && value !== '';
}

const INVENTORY_HEAL_POLICY_ROLL_IDS = Object.freeze([
  '4FB7452A-F130-40C3-83E6-5E92C329D7B2', // healing roll
  '9909BE44-99AE-8780-BB12-BD99BA6E672E', // regeneration roll
  '5E2689A2-0F7D-1E27-F4D5-CA09CA5C2591', // temporary vitality roll
  '4634635E-4E82-DFB3-8411-27A6BFB8CF0F', // temporary vitality regeneration roll
]);

/**
 * Whether a single-target ability may opt in to world-object (inventory) targets — any roll type,
 * not just attacks (owner 2026-09-10: projectile spells too). Objects never resist: Janus makes no
 * attack roll against inventory and synthesizes a passed contest (damage_roll.php), so the only
 * exclusions are abilities an object can never legitimately receive. Mirrors the AOE inventory
 * policy (AOETargetResolver + is_target_valid_for_effect): teamless props fail team gates, heals
 * skip them unless the ability repairs inventory. Creature-classification and required-status
 * gates are excluded here because ability_target_gate_failure() waves inventory through them.
 */
export function abilityAllowsInventoryTargets(blob: unknown): boolean {
  if (!recordOf(blob) || String(itemField(blob, 'item_type') ?? '') === 'skill') return false;
  if ([
    'effect_only_affects_opposing_team_type', 'target_opposing_team_only', 'target_friendly_only',
    'ability_works_only_against_item_type', 'ability_does_not_effect_inventory',
    'ability_works_only_against_creature_type', 'ability_works_only_against_creature_sub_type',
    'ability_works_only_against_creature_lineage', 'ability_works_only_against_creature_ancestry',
    'ability_works_only_against_creature_grouping', 'if_target_has_status_effect_active',
  ].some(key => enabled(layerValue(blob, key)))) return false;
  const repairsInventory = enabled(layerValue(blob, 'effect_can_heal_inventory'))
    || enabled(layerValue(blob, 'effect_can_only_heal_inventory'));
  const healPolicyRoll = INVENTORY_HEAL_POLICY_ROLL_IDS
    .includes(String(layerValue(blob, 'roll_type') ?? '').trim().toUpperCase());
  if (healPolicyRoll && !repairsInventory) return false;
  return !enabled(layerValue(blob, 'effect_only_affects_same_team_type')) || (healPolicyRoll && repairsInventory);
}

export function classifyAbilityDispatchVariant(facts: AbilityDispatchFacts): AbilityDispatchClassification {
  if (facts.aoe) return { route: 'legacy', reason: 'aoe' };
  if (facts.itemTarget) return { route: 'legacy', reason: 'item-target' };
  if (facts.weaponPinned) return { route: 'legacy', reason: 'weapon-pinned' };
  if (facts.trigger) return { route: 'legacy', reason: 'trigger' };
  if (facts.dedicated) return { route: 'legacy', reason: 'dedicated' };
  if (facts.markerOrSource) return { route: 'legacy', reason: 'marker-or-source' };
  if (facts.specialized) return { route: 'legacy', reason: 'specialized' };
  const inputId = String(facts.inputId ?? '').trim();
  const inputKind = facts.inputKind
    ?? (inputId.endsWith('_spell_upcastable') ? 'spell-level'
      : inputId.endsWith('_user_choice_1_damage_type') ? 'damage-type'
        : 'other');
  // Combined option + creature targeting stays deliberately narrow: numeric spell upcasts and the
  // ordinary authored damage-type picker both occupy variable_1 and compose cleanly with target_id.
  // Other selects/counts can have branch, source, ritual, or variable_2 semantics and remain legacy
  // until their invocation shape is classified explicitly.
  if (inputId) {
    if (inputKind === 'spell-level' || inputKind === 'numeric') {
      return { route: 'ability-targeting', invocation: { kind: 'variable-1-number', inputId } };
    }
    if (inputKind === 'damage-type') {
      return { route: 'ability-targeting', invocation: { kind: 'variable-1-choice', inputId } };
    }
    return { route: 'legacy', reason: 'option-bearing' };
  }
  return { route: 'ability-targeting', invocation: { kind: 'plain' } };
}

/** Client ceiling implied by an option that already occupies variable_1. Janus treats max_targets [x]
 * as that literal value for every numeric family; leveled spells may instead add targets per level. */
export function deriveAbilityTargetingNumericMaxTargets(
  blob: unknown,
  optionKind: AbilityTargetingNumericOptionKind,
  rawValue: unknown,
): number | undefined {
  const value = Number(rawValue);
  if (!Number.isFinite(value) || !Number.isInteger(value) || value < 0) return undefined;
  if (layerValue(blob, 'max_targets') === '[x]') return value;
  if (optionKind !== 'spell-level' || !layerPresent(blob, 'spell_upcast_additional_targets_int')) {
    return undefined;
  }
  const baseTargets = Math.max(1, Math.trunc(Number(layerValue(blob, 'max_targets')) || 1));
  const baseLevel = Math.max(0, Math.trunc(Number(layerValue(blob, 'spell_level')) || 0));
  const levels = Math.max(0, value - baseLevel);
  const targetsPerLevel = Math.max(0,
    Math.trunc(Number(layerValue(blob, 'spell_upcast_additional_targets_int')) || 0));
  return baseTargets + levels * targetsPerLevel;
}

/**
 * Conservative client profile for the first ability-first slice. It admits only an ordinary,
 * explicitly ranged, non-AOE creature pick with a fixed target ceiling. Every server-owned
 * automatic, self/object, item-picker, targetless fabrication, and runtime-count grammar stays
 * on the pre-existing dispatch path.
 */
/** True when every `ability_works_only_against_item_type` entry is `creature` — a creature-target gate,
 *  not an own-inventory picker. Mirrors the carryable split in ownInventoryTargetTypes (action-bar.ts). */
export function abilityItemTypeGateIsCreatureOnly(blob: unknown): boolean {
  // Same lookup order as layerValue()/layerPresent(): the item_layer_types bucket, then the root key.
  const root = recordOf(blob);
  const layers = recordOf(root?.item_layer_types);
  const raw = layers?.['ability_works_only_against_item_type'] ?? root?.['ability_works_only_against_item_type'];
  const entries = (Array.isArray(raw) ? raw.flat(Infinity) : raw === undefined || raw === null ? [] : [raw])
    .filter(entry => typeof entry === 'string' || typeof entry === 'number')
    .map(entry => String(entry).trim().toLowerCase())
    .filter(Boolean);
  return entries.length > 0 && entries.every(type => type === 'creature');
}

export const PILOTABLE_ITEM_TYPES = ['vehicle_land', 'vehicle_water', 'vehicle_air', 'war_machine'] as const;

/** An object OPERATED IN PLACE like a war machine: every war_machine, plus any item carrying the `operable_object`
 *  layer (piano, harpsichord, pipe organ, floor harps … 2026-09-30). Twin of ft_item_is_operable_object() (PHP). */
export const OPERABLE_OBJECT_LAYER = 'operable_object';
export function isOperableObject(itemType: string | null | undefined, operableLayer: unknown): boolean {
  if ((itemType ?? '') === 'war_machine') return true;
  const raw = Array.isArray(operableLayer) ? operableLayer[0] : operableLayer;
  return Number(raw ?? 0) === 1;
}
/** An item-type gate admits a target when its type is listed — or, when the gate names war_machine, when the
 *  target is an operable object (Pilot a Vehicle → Operate the piano). */
export function itemTypeGateAdmits(allowed: readonly string[], itemType: string | null | undefined, operable: boolean): boolean {
  if (allowed.includes(itemType ?? '')) return true;
  return operable && allowed.includes('war_machine');
}

/** Map-placed object types an item-type gate may name as the ability's TARGET — never a caster's carried gear.
 *  Twin of ABTN_WORLD_OBJECT_TYPES (ability-button.ts) and NON_CARRYABLE_ITEM_TYPES (action-bar.ts). */
export const WORLD_OBJECT_TARGET_ITEM_TYPES = [
  'furniture', 'door', 'building', 'window', 'wall', 'roof', 'floor',
  ...PILOTABLE_ITEM_TYPES, 'ore_vein', 'tree', 'trap', 'digsite',
] as const;

/** A gate made only of world-object types (Ram the Gate → door / building / war_machine / vehicle_land) rides the
 *  ability-first scene pick restricted to those types. It used to admit pilotable types only, so a mixed gate fell to
 *  the legacy dispatch that demanded the object be selected BEFORE the ability (2026-09-16). */
export function abilityWorldObjectTargetTypes(blob: unknown): string[] {
  const root = recordOf(blob);
  const layers = recordOf(root?.item_layer_types);
  const raw = layers?.ability_works_only_against_item_type ?? root?.ability_works_only_against_item_type;
  const types = (Array.isArray(raw) ? raw.flat(Infinity) : [raw])
    .map(value => String(value ?? '').trim().toLowerCase()).filter(Boolean);
  return types.length && types.every(type => (WORLD_OBJECT_TARGET_ITEM_TYPES as readonly string[]).includes(type))
    ? [...new Set(types)] : [];
}

const RANGE_UNLIMITED_ID = 'BB47D2E8-FFFD-8A04-FC6C-FC02583750ED';

/** Targeting profile for a CONTESTED innate skill (Influence vs Empathy, Frighten vs Empathy, Sneak vs
 *  Attention …). Skill items carry no `range`, so deriveAbilityTargetingProfile rejects them; the server
 *  (template_contesting_roll) contests against a targeted creature when one is sent and otherwise rolls
 *  against the GM's fixed DC — hence minTargets 0: confirming with no pick is the plain roll. */
export function deriveContestedSkillTargetingProfile(contestTargetName: string): AbilityTargetingProfile {
  return {
    kind: 'creature-targets',
    // Contests still need visible caster-to-target/LOS communication. This neutral guide does not
    // classify the roll as an attack and therefore never enables attack hit-chance estimates.
    aimLine: 'line-of-effect',
    minTargets: 0,
    maxTargets: 1,
    rangeId: RANGE_UNLIMITED_ID,
    rangeFeet: null,
    rangeLabel: contestTargetName ? `Contested vs ${contestTargetName}` : 'Contested roll',
  };
}

export function deriveAbilityTargetingProfile(blob: unknown): AbilityTargetingProfileResult {
  const abilityId = String(itemField(blob, 'item_id') ?? '').trim();
  if (!abilityId) return { supported: false, reason: 'missing-ability-id' };
  if (enabled(layerValue(blob, 'aoe_type')) || enabled(layerValue(blob, 'persistent_aoe'))) {
    return { supported: false, reason: 'aoe' };
  }
  // An item-type gate normally means "pick one of my carried items" (imbue a weapon). A gate that
  // names ONLY `creature` is the opposite: it describes the creature being targeted (Mount a Creature,
  // Force Mount), so it rides the ordinary creature-targeting lane instead of the item picker.
  const inventoryTargetTypes = abilityWorldObjectTargetTypes(blob);
  if (layerPresent(blob, 'ability_works_only_against_item_type') && !abilityItemTypeGateIsCreatureOnly(blob) && !inventoryTargetTypes.length) {
    return { supported: false, reason: 'item-target' };
  }
  if (AUTO_TARGET_LAYERS.some(layer => layerPresent(blob, layer))) {
    return { supported: false, reason: 'auto-target' };
  }
  const rangeId = String(layerValue(blob, 'range') ?? '').trim().toUpperCase();
  if (!rangeId) return { supported: false, reason: 'targetless-or-implicit' };
  if (rangeId === RANGE_SELF_ID) return { supported: false, reason: 'self-target' };
  if (rangeId === RANGE_OBJECT_ID) return { supported: false, reason: 'object-target' };
  if (!Object.prototype.hasOwnProperty.call(RANGE_FEET_BY_ID, rangeId)) {
    return { supported: false, reason: 'unresolved-range' };
  }

  const rawMax = layerValue(blob, 'max_targets');
  if (rawMax === '[x]') return { supported: false, reason: 'runtime-target-count' };
  // A CHAIN host (chain_targets_max, 2026-10-07 — janus-engine/ability/rolls/chain-leap.ts): the primary, then up to N creatures it leaps
  // to, picked in that order. The chain's 1 + N is the ceiling (an authored max_targets does not widen it — resolution takes no more).
  const chainRaw = Number(layerValue(blob, 'chain_targets_max') ?? 0);
  const chainExtra = Number.isInteger(chainRaw) && chainRaw >= 1 ? chainRaw : 0;
  const maxTargets = chainExtra > 0 ? 1 + chainExtra : rawMax === undefined || rawMax === null || rawMax === ''
    ? 1
    : Number(rawMax);
  if (!Number.isInteger(maxTargets) || maxTargets < 1 || maxTargets > 1000) {
    return { supported: false, reason: 'invalid-target-count' };
  }
  const rangeFeet = RANGE_FEET_BY_ID[rangeId] ?? null;
  const rollTypeId = String(layerValue(blob, 'roll_type') ?? '').trim().toUpperCase();
  const rangeLabel = rangeId === '36D59FF4-6778-96ED-8664-F5D63638C77B'
    ? 'Reach'
    : rangeId === 'BB47D2E8-FFFD-8A04-FC6C-FC02583750ED'
      ? 'Unlimited'
      : rangeFeet === null ? 'Range set by server' : `${rangeFeet} ft`;
  return {
    supported: true,
    profile: {
      kind: 'creature-targets',
      ...(inventoryTargetTypes.length ? { inventoryTargetTypes, allowInventoryTargets: true } : {}),
      aimLine: RANGED_ATTACK_ROLL_IDS.includes(rollTypeId)
        ? 'ranged-attack'
        : MELEE_ATTACK_ROLL_IDS.includes(rollTypeId)
          ? 'melee-attack'
          : rollTypeId === CONTESTING_ROLL_ID ? 'line-of-effect' : 'none',
      projectileKind: paintedWallProjectileKind(rollTypeId, abilityId),
      minTargets: 1,
      maxTargets,
      rangeId,
      rangeFeet,
      rangeLabel,
    },
  };
}

function normalizeIds(values: readonly string[]): string[] {
  return [...new Set(values.map(value => String(value).trim().toUpperCase()).filter(Boolean))];
}

function cloneIntent(intent: ActiveAbilityTargetingIntent): ActiveAbilityTargetingIntent {
  return {
    ...intent,
    profile: { ...intent.profile, ...(intent.profile.projectilePool ? { projectilePool: { ...intent.profile.projectilePool } } : {}) },
    invocation: { ...intent.invocation },
    selectedEntityIds: [...intent.selectedEntityIds],
    candidateEntityIds: [...intent.candidateEntityIds],
    ...(intent.grantSourceIds ? { grantSourceIds: [...intent.grantSourceIds] } : {}),
    ...(intent.rangeOriginIds ? { rangeOriginIds: [...intent.rangeOriginIds] } : {}),
    ...(intent.rangeOriginFeet ? { rangeOriginFeet: [...intent.rangeOriginFeet] } : {}),
  };
}

export class AbilityTargetingIntentLifecycle {
  private serial = 0;
  private activeIntent: ActiveAbilityTargetingIntent | null = null;

  get version(): number { return this.serial; }

  current(): ActiveAbilityTargetingIntent | null {
    return this.activeIntent ? cloneIntent(this.activeIntent) : null;
  }

  arm(input: Omit<ActiveAbilityTargetingIntent, 'intentId' | 'phase' | 'selectedEntityIds'>): ActiveAbilityTargetingIntent {
    this.serial += 1;
    this.activeIntent = {
      ...input,
      intentId: this.serial,
      phase: 'selecting-targets',
      selectedEntityIds: [],
      candidateEntityIds: normalizeIds(input.candidateEntityIds),
      grantSourceIds: input.grantSourceIds?.length ? normalizeIds(input.grantSourceIds) : undefined,
      rangeOriginIds: input.rangeOriginIds?.length ? normalizeIds(input.rangeOriginIds) : undefined,
      rangeOriginFeet: input.rangeOriginIds?.length && input.rangeOriginFeet ? [...input.rangeOriginFeet] : undefined,
      profile: { ...input.profile },
      invocation: { ...input.invocation },
    };
    this.syncPhase(); // minTargets 0 (contested skill) is ready-to-confirm with nothing picked
    return cloneIntent(this.activeIntent);
  }

  updateCandidates(intentId: number, candidateEntityIds: readonly string[]): ActiveAbilityTargetingIntent | null {
    if (!this.activeIntent || this.activeIntent.intentId !== intentId) return null;
    const candidates = normalizeIds(candidateEntityIds);
    const allowed = new Set(candidates);
    this.activeIntent.candidateEntityIds = candidates;
    this.activeIntent.selectedEntityIds = this.activeIntent.selectedEntityIds.filter(id => allowed.has(id));
    this.syncPhase();
    return cloneIntent(this.activeIntent);
  }

  setInventoryTargets(intentId: number, enabled: boolean, candidateEntityIds: readonly string[]): ActiveAbilityTargetingIntent | null {
    if (!this.activeIntent || this.activeIntent.intentId !== intentId
      || !this.activeIntent.profile.supportsInventoryTargets) return null;
    this.activeIntent.profile.allowInventoryTargets = enabled;
    return this.updateCandidates(intentId, candidateEntityIds);
  }

  pruneSelected(intentId: number, allowedEntityIds: readonly string[]): ActiveAbilityTargetingIntent | null {
    if (!this.activeIntent || this.activeIntent.intentId !== intentId) return null;
    const allowed = new Set(normalizeIds(allowedEntityIds));
    this.activeIntent.selectedEntityIds = this.activeIntent.selectedEntityIds.filter(id => allowed.has(id));
    this.syncPhase();
    return cloneIntent(this.activeIntent);
  }

  updateNumericInvocation(
    intentId: number,
    rawValue: number,
    rawMaxTargets?: number,
  ): ActiveAbilityTargetingIntent | null {
    if (!this.activeIntent || this.activeIntent.intentId !== intentId
      || this.activeIntent.invocation.kind !== 'variable-1-number') return null;
    const value = Number(rawValue);
    if (!Number.isInteger(value) || value < 0) return null;
    const maxTargets = rawMaxTargets === undefined ? null : Number(rawMaxTargets);
    if (maxTargets !== null && (!Number.isInteger(maxTargets)
      || maxTargets < this.activeIntent.profile.minTargets || maxTargets > 1000)) return null;
    this.activeIntent.invocation.value = value;
    if (maxTargets !== null) {
      this.activeIntent.profile.maxTargets = maxTargets;
      this.activeIntent.selectedEntityIds = this.activeIntent.selectedEntityIds.slice(0, maxTargets);
      this.syncPhase();
    }
    return cloneIntent(this.activeIntent);
  }

  updateChoiceInvocation(intentId: number, rawValue: string): ActiveAbilityTargetingIntent | null {
    if (!this.activeIntent || this.activeIntent.intentId !== intentId
      || this.activeIntent.invocation.kind !== 'variable-1-choice') return null;
    const value = String(rawValue ?? '').trim();
    if (!value) return null;
    this.activeIntent.invocation.value = value;
    return cloneIntent(this.activeIntent);
  }

  updateSecondaryChoice(intentId: number, rawValue: string): ActiveAbilityTargetingIntent | null {
    if (!this.activeIntent || this.activeIntent.intentId !== intentId
      || this.activeIntent.invocation.kind !== 'variable-1-number') return null;
    const value = String(rawValue ?? '').trim();
    if (!value) return null;
    this.activeIntent.invocation.secondaryChoice = value;
    return cloneIntent(this.activeIntent);
  }

  stagePick(intentId: number, rawEntityId: string, remove = false): ActiveAbilityTargetingIntent | null {
    if (!this.activeIntent || this.activeIntent.intentId !== intentId) return null;
    const entityId = String(rawEntityId ?? '').trim().toUpperCase();
    if (!entityId || !this.activeIntent.candidateEntityIds.includes(entityId)) return null;
    const selected = [...this.activeIntent.selectedEntityIds];
    const index = selected.indexOf(entityId);
    if (index >= 0 && (!this.activeIntent.profile.projectilePool || remove)) selected.splice(index, 1);
    else if (selected.length < this.activeIntent.profile.maxTargets) selected.push(entityId);
    else return null;
    this.activeIntent.selectedEntityIds = selected;
    this.syncPhase();
    return cloneIntent(this.activeIntent);
  }

  /** Held-item imbue: the selection is one inventory row, which is never a candidate (candidates are its
   *  holders). The caller has already proven the row is an allowed item a legal candidate holds. */
  selectHeldItem(intentId: number, rawInventoryId: string): ActiveAbilityTargetingIntent | null {
    if (!this.activeIntent || this.activeIntent.intentId !== intentId
      || !this.activeIntent.profile.heldItemTargetTypes?.length) return null;
    const inventoryId = String(rawInventoryId ?? '').trim().toUpperCase();
    if (!inventoryId) return null;
    this.activeIntent.selectedEntityIds = [inventoryId];
    this.syncPhase();
    return cloneIntent(this.activeIntent);
  }

  consumeConfirmation(intentId?: number): ActiveAbilityTargetingIntent | null {
    if (!this.activeIntent || (intentId !== undefined && this.activeIntent.intentId !== intentId)) return null;
    if (this.activeIntent.phase !== 'ready-to-confirm') return null;
    const confirmed = cloneIntent(this.activeIntent);
    this.activeIntent = null;
    this.serial += 1;
    return confirmed;
  }

  cancel(): boolean {
    if (!this.activeIntent) return false;
    this.activeIntent = null;
    this.serial += 1;
    return true;
  }

  envelope(): AbilityTargetingIntentEnvelope {
    const intent = this.current();
    return {
      schema: ABILITY_TARGETING_SCHEMA,
      version: this.serial,
      intent: intent ? {
        intentId: intent.intentId,
        casterId: intent.casterId,
        abilityId: intent.abilityId,
        abilityName: intent.abilityName,
        actionPointCost: intent.actionPointCost,
        profile: intent.profile,
        phase: intent.phase,
        selectedEntityIds: intent.selectedEntityIds,
        candidateEntityIds: intent.candidateEntityIds,
        ...(intent.grantSourceIds?.length ? { grantSourceIds: intent.grantSourceIds } : {}),
        ...(intent.rangeOriginIds?.length ? { rangeOriginIds: intent.rangeOriginIds, ...(intent.rangeOriginFeet ? { rangeOriginFeet: intent.rangeOriginFeet } : {}) } : {}),
      } : null,
    };
  }

  private syncPhase(): void {
    if (!this.activeIntent) return;
    this.activeIntent.phase = this.activeIntent.selectedEntityIds.length >= this.activeIntent.profile.minTargets
      ? 'ready-to-confirm'
      : 'selecting-targets';
  }
}

export function parseAbilityTargetingIntentEnvelope(value: unknown): AbilityTargetingIntentEnvelope | null {
  const root = recordOf(value);
  if (root?.schema !== ABILITY_TARGETING_SCHEMA || !Number.isInteger(root.version) || Number(root.version) < 0) return null;
  if (root.intent === null) return { schema: ABILITY_TARGETING_SCHEMA, version: Number(root.version), intent: null };
  const intent = recordOf(root.intent);
  const profile = recordOf(intent?.profile);
  if (!intent || !profile || !Number.isInteger(intent.intentId)
    || !['selecting-targets', 'ready-to-confirm'].includes(String(intent.phase))) return null;
  const minTargets = Number(profile.minTargets);
  const maxTargets = Number(profile.maxTargets);
  const projectilePool = parseProjectilePool(profile.projectilePool);
  if (profile.projectilePool !== undefined && (!projectilePool || projectilePool.remaining !== maxTargets)) return null;
  if (profile.kind !== 'creature-targets' || !Number.isInteger(minTargets) || !Number.isInteger(maxTargets)
    || minTargets < 0 || maxTargets < 1 || maxTargets < minTargets) return null;
  return {
    schema: ABILITY_TARGETING_SCHEMA,
    version: Number(root.version),
    intent: {
      intentId: Number(intent.intentId),
      casterId: String(intent.casterId ?? ''),
      abilityId: String(intent.abilityId ?? ''),
      abilityName: String(intent.abilityName ?? 'Ability'),
      actionPointCost: intent.actionPointCost !== null && intent.actionPointCost !== undefined
        && Number.isFinite(Number(intent.actionPointCost))
        ? Math.max(0, Math.trunc(Number(intent.actionPointCost)))
        : null,
      phase: String(intent.phase) as ActiveAbilityTargetingIntent['phase'],
      selectedEntityIds: projectilePool
        ? (Array.isArray(intent.selectedEntityIds) ? intent.selectedEntityIds.map(id => String(id).trim().toUpperCase()).filter(Boolean).slice(0, maxTargets) : [])
        : normalizeIds(Array.isArray(intent.selectedEntityIds) ? intent.selectedEntityIds.map(String) : []),
      candidateEntityIds: normalizeIds(Array.isArray(intent.candidateEntityIds) ? intent.candidateEntityIds.map(String) : []),
      ...(Array.isArray(intent.grantSourceIds) && intent.grantSourceIds.length
        ? { grantSourceIds: normalizeIds(intent.grantSourceIds.map(String)) }
        : {}),
      ...(Array.isArray(intent.rangeOriginIds) && intent.rangeOriginIds.length
        ? { rangeOriginIds: intent.rangeOriginIds.map(id => String(id).trim().toUpperCase()).filter(Boolean),
            ...(Array.isArray(intent.rangeOriginFeet) && intent.rangeOriginFeet.length === intent.rangeOriginIds.length
              ? { rangeOriginFeet: intent.rangeOriginFeet.map(v => (v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v))) } : {}) }
        : {}),
      profile: {
        kind: 'creature-targets',
        allowInventoryTargets: profile.allowInventoryTargets === true,
        inventoryTargetTypes: Array.isArray(profile.inventoryTargetTypes)
          ? profile.inventoryTargetTypes.map(String).filter(type => (WORLD_OBJECT_TARGET_ITEM_TYPES as readonly string[]).includes(type)) : undefined,
        ...(Array.isArray(profile.heldItemTargetTypes) && profile.heldItemTargetTypes.length
          ? { heldItemTargetTypes: [...new Set(profile.heldItemTargetTypes.map(type => String(type).trim().toLowerCase()).filter(Boolean))] }
          : {}),
        aimLine: normalizeAbilityTargetingAimLine(profile.aimLine),
        projectileKind: profile.projectileKind === 'ordinary' || profile.projectileKind === 'massive' || profile.projectileKind === 'spell'
          ? profile.projectileKind : 'none',
        minTargets,
        maxTargets,
        ...(projectilePool ? { projectilePool } : {}),
        rangeId: String(profile.rangeId ?? ''),
        rangeFeet: profile.rangeFeet !== null && profile.rangeFeet !== undefined
          && Number.isFinite(Number(profile.rangeFeet)) ? Number(profile.rangeFeet) : null,
        rangeLabel: String(profile.rangeLabel ?? 'Range set by server'),
        // A lobbed weapon throw (owner 2026-09-15). ⚠ This parser is a WHITELIST: an intent field that is not copied
        // here silently vanishes in the scene (the arcing-trajectory build hit the same trap on the AOE contract).
        ...(profile.throwLob === true ? { throwLob: true } : {}),
        ...(recordOf(profile.weaponAttack)?.inventoryId ? { weaponAttack: {
          inventoryId: String(recordOf(profile.weaponAttack)!.inventoryId).trim().toUpperCase(),
          throwing: recordOf(profile.weaponAttack)!.throwing === true,
        } } : {}),
      },
    },
  };
}


export function parseAbilityTargetingCandidatesEnvelope(value: unknown): AbilityTargetingCandidatesEnvelope | null {
  const root = recordOf(value);
  if (root?.schema !== ABILITY_TARGETING_CANDIDATES_SCHEMA
    || !Number.isInteger(root.version) || Number(root.version) < 1
    || !Array.isArray(root.entityIds)) return null;
  const candidates = Array.isArray(root.candidates) ? root.candidates.flatMap((raw): AbilityTargetingCandidateFact[] => {
    const candidate = recordOf(raw);
    const entityId = String(candidate?.entityId ?? '').trim().toUpperCase();
    const rangeState = String(candidate?.rangeState ?? '') as AbilityTargetingRangeState;
    const lineOfEffect = String(candidate?.lineOfEffect ?? 'clear');
    const coverTier = String(candidate?.coverTier ?? 'none') as AbilityTargetingCoverTier;
    const delta = Number(candidate?.effectiveDefenseDelta ?? 0);
    if (!entityId || !['in-range', 'out-of-range', 'unknown'].includes(rangeState)
      || !['clear', 'blocked'].includes(lineOfEffect)
      || !['none', 'partial', 'heavy', 'blocked'].includes(coverTier)
      || !Number.isFinite(delta)) return [];
    return [{
      entityId,
      targetable: candidate?.targetable === true,
      rangeState,
      lineOfEffect: lineOfEffect as AbilityTargetingCandidateFact['lineOfEffect'],
      coverTier,
      effectiveDefenseDelta: Math.trunc(delta),
      hunkerApplied: candidate?.hunkerApplied === true,
      flanked: candidate?.flanked === true,
      flankingAllyName: typeof candidate?.flankingAllyName === 'string' && candidate.flankingAllyName.trim() !== ''
        ? candidate.flankingAllyName.trim()
        : null,
    }];
  }) : [];
  return {
    schema: ABILITY_TARGETING_CANDIDATES_SCHEMA,
    version: Number(root.version),
    entityIds: normalizeIds(root.entityIds.map(String)),
    candidates,
  };
}

export function parseAbilityTargetingFeedbackEnvelope(value: unknown): AbilityTargetingFeedbackEnvelope | null {
  const root = recordOf(value);
  const reason = String(root?.reason ?? '') as AbilityTargetingFeedbackReason;
  if (root?.schema !== ABILITY_TARGETING_FEEDBACK_SCHEMA
    || !Number.isInteger(root.version) || Number(root.version) < 0
    || !['out-of-range', 'blocked-line-of-effect', 'incompatible-target', 'max-targets'].includes(reason)) return null;
  const message = typeof root?.message === 'string' ? root.message.trim() : '';
  return {
    schema: ABILITY_TARGETING_FEEDBACK_SCHEMA,
    version: Number(root.version),
    reason,
    ...(message ? { message } : {}),
  };
}

export function parseAbilityTargetingEstimatesEnvelope(value: unknown): AbilityTargetingEstimatesEnvelope | null {
  const root = recordOf(value);
  if (root?.schema !== ABILITY_TARGETING_ESTIMATES_SCHEMA
    || !Number.isInteger(root.version) || Number(root.version) < 1
    || !Array.isArray(root.estimates)) return null;
  const estimates = root.estimates.flatMap((raw): AbilityTargetingAimEstimate[] => {
    const estimate = recordOf(raw);
    const entityId = String(estimate?.entityId ?? '').trim().toUpperCase();
    const hitChancePercent = Number(estimate?.hitChancePercent);
    const advDisStacks = estimate?.advDisStacks === undefined ? 0 : Number(estimate.advDisStacks);
    if (!entityId || !Number.isFinite(hitChancePercent)
      || hitChancePercent < 0 || hitChancePercent > 100
      || !Number.isFinite(advDisStacks)) return [];
    return [{
      entityId,
      hitChancePercent: Math.round(hitChancePercent),
      advDisStacks: Math.max(-100, Math.min(100, Math.trunc(advDisStacks))),
    }];
  });
  return {
    schema: ABILITY_TARGETING_ESTIMATES_SCHEMA,
    version: Number(root.version),
    estimates,
  };
}
